import axios from "axios";
import format from "date-fns/format";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useTires from "fe-shared/src/hooks/useTires";
import useProducts from "fe-shared/src/hooks/useProducts";
import useNonEligibleMaterials from "fe-shared/src/hooks/useNonEligibleMaterials";
import { getOffsetDate } from "fe-shared/src/helpers/dates";

import { BusinessRole, MaterialUnit } from "fe-shared/src/types/domain";
import { ExcelColumnConfig } from "fe-shared/src/types/common";
import { formatRecord, formatTransactionNumber } from "../helpers/format";

import {
  DataGridRequestParams,
  GridTransferExport,
  NoteRecord,
  TransferResponse,
} from "../types";

interface HistoryLogsResponse {
  [key: string]: NoteRecord[];
}

const getFormattedDate = (dateString?: string) => {
  if (!dateString) {
    return "";
  }
  const time = dateString?.substr(11, 8);
  const isNoOffset = time === "00:00:00";

  const date = isNoOffset
    ? getOffsetDate(new Date(dateString))!
    : new Date(dateString);

  return format(date, "MMM d, yyyy");
};

export const getTransfersExport = async (
  requestParams: DataGridRequestParams,
  companyId?: number
) => {
  const transfers = await axios
    .post<TransferResponse<GridTransferExport>>(
      "getTransfers/export",
      requestParams,
      { params: { companyId } }
    )
    .then((res) => res.data.transfers);

  if (!companyId) {
    const transferNumbers = transfers.map((i) => i.transferNumber);
    const historyLogsByInvoiceNumber = await axios
      .post<HistoryLogsResponse>("history/fetchForExport", {
        objectIds: transferNumbers,
        entityType: "transfer",
      })
      .then((res) => res.data);

    return transfers.map((t) => ({
      ...t,
      historyLogs: historyLogsByInvoiceNumber[t.transferNumber],
    }));
  }

  return transfers;
};

const useTransfersExport = (
  requestParams: DataGridRequestParams,
  companyId?: number
) => {
  const { t } = useTranslation();
  const { data } = useTires();
  const { data: products } = useProducts();
  const { data: nonEligibleItems } = useNonEligibleMaterials();
  const isStaff = !companyId;

  const tires = useMemo(
    () =>
      (data || []).filter((tire) =>
        tire.allowedRoles?.includes(BusinessRole.Processor)
      ),
    [data]
  );

  const getDataRows = () => getTransfersExport(requestParams, companyId);

  const columnConfig = useMemo(() => {
    const columns: ExcelColumnConfig<GridTransferExport>[] = [
      {
        width: 15,
        mappedKey: t("transfers.grid.export.cols.transferNumber"),
        getOriginalValue: (i) => formatTransactionNumber(i.transferNumber, ""),
      },
      {
        width: 15,
        mappedKey: t("transfers.grid.export.cols.participantRegNumber"),
        getOriginalValue: (i) => i.participantRegNumber,
      },
      {
        width: 25,
        mappedKey: t("transfers.grid.export.cols.participantName"),
        getOriginalValue: (i) => i.participantDisplayName,
      },
      ...(!isStaff
        ? [
            {
              width: 20,
              mappedKey: t("transfers.grid.export.cols.sortYardId"),
              getOriginalValue: (i: GridTransferExport) =>
                i.sortYardId > 0
                  ? t("transfers.grid.export.cols.sortYardIdValue", {
                      id: i.sortYardId,
                    })
                  : "",
            },
          ]
        : []),
      {
        width: 15,
        mappedKey: t("transfers.grid.export.cols.completedDate"),
        getOriginalValue: (i) => getFormattedDate(i.completedDate),
      },
      {
        width: 15,
        mappedKey: t("transfers.grid.export.cols.syncedOn"),
        getOriginalValue: (i) =>
          i.syncedDate ? getFormattedDate(i.syncedDate) : "",
      },
      {
        width: 20,
        mappedKey: t("transfers.grid.export.cols.type"),
        getOriginalValue: (i) => t(`transfers.type.${i.transferType}`),
      },
      {
        width: 15,
        mappedKey: t("transfers.grid.export.cols.status"),
        getOriginalValue: (i) => t(`transfers.statuses.${i.statusCode}`),
      },
      {
        width: 15,
        mappedKey: t("transfers.grid.export.cols.createdBy"),
        getOriginalValue: (i) => i.createdBy,
      },
      ...tires.map((tire) => ({
        width: 15,
        mappedKey: t("transfers.grid.export.cols.tireCount", {
          code: tire.code,
        }),
        getOriginalValue: (i: GridTransferExport) =>
          i.materials?.find(
            (m) =>
              m.materialCode === tire.code &&
              m.units.unit === MaterialUnit.Units
          )?.units.unitQuantity,
      })),
      {
        width: 30,
        mappedKey: t("transfers.grid.export.cols.totalWeight"),
        getOriginalValue: (i) => i.totalWeight,
      },
      ...tires.map((tire) => ({
        width: 15,
        mappedKey: t("transfers.grid.export.cols.materialWeight", {
          name: tire.code,
        }),
        getOriginalValue: (i: GridTransferExport) =>
          i.materials?.find(
            (m) =>
              m.materialCode === tire.code && m.units.unit === MaterialUnit.Kg
          )?.units.unitQuantity,
      })),
      ...(nonEligibleItems || []).map((item) => ({
        width: 15,
        mappedKey: t("transfers.grid.export.cols.materialWeight", {
          name: item.name,
        }),
        getOriginalValue: (i: GridTransferExport) =>
          i.nonEligibleMaterial &&
          i.nonEligibleMaterial.materialCode === item.code
            ? i.nonEligibleMaterial?.units.unitQuantity
            : "",
      })),
      ...(products || []).map((product) => ({
        width: 15,
        mappedKey: t("transfers.grid.export.cols.materialWeight", {
          name: product.name,
        }),
        getOriginalValue: (i: GridTransferExport) =>
          i.materials?.find(
            (m) =>
              m.materialCode === product.code &&
              m.units.unit === MaterialUnit.Kg
          )?.units.unitQuantity,
      })),
      ...(isStaff
        ? [
            {
              width: 30,
              mappedKey: t("transfers.grid.export.cols.isEdited"),
              getOriginalValue: (i: GridTransferExport) =>
                t(i.isEdited ? "yes" : "no"),
            },
            {
              width: 20,
              mappedKey: t("transfers.grid.export.cols.notes"),
              getOriginalValue: (i: GridTransferExport) =>
                (i.notes || []).map(formatRecord).join("\n"),
            },
            {
              width: 20,
              mappedKey: t("transfers.grid.export.cols.historyLogs"),
              getOriginalValue: (i: GridTransferExport) =>
                (i.historyLogs || []).map(formatRecord).join("\n"),
            },
          ]
        : []),
    ];

    return columns;
  }, [t, tires, products, nonEligibleItems, isStaff]);

  return {
    getDataRows,
    columnConfig,
  };
};

export default useTransfersExport;
